import { Gender, PetType, VehicleType } from "./enums";

export type ContractSignatureStatus = "SIGNED" | "REJECTED" | "CANCELLED";

export interface ErrorApiDto {
  name: string;
  code: number;
  message: string;
  errorId: string;
}

export enum LanguageShortApiEnum {
  en = 'en',
  es = 'es'
}

export enum LanguageApiEnum {
  SPANISH = 'SPANISH',
  ENGLISH = 'ENGLISH'
}

export enum CountryApiEnum {
  ECUADOR = 'ECUADOR',
  USA = 'USA',
  CANADA = 'CANADA',
  UK = 'UK',
  GERMANY = 'GERMANY',
}

export enum MimeApiEnum {
  'image/jpeg' = 'image/jpeg',
  'image/png' = 'image/png'
}

export enum GuestPictureTypeApiEnum {
  ID_FRONT = 'ID_FRONT',
  ID_BACK = 'ID_BACK',
  SELFIE = 'SELFIE',
  OTHER = 'OTHER'
}

export enum IdTypeApiEnum {
  PASSPORT = 'PASSPORT',
  NATIONAL_ID = 'NATIONAL_ID'
}

export enum FormStatusApiEnum {
  SENT = 'SENT',
  COMPLETED = 'COMPLETED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  VALIDATED = 'VALIDATED',
  INVALIDATED = 'INVALIDATED'
}

export interface PublicFormDataApiDto {
  templateId: string;
  listingTitle: string;
  listingCity: string;
  listingCountry: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  maxVehicles: number;
  allowPets: boolean;
  listingAddress: string;
  status: FormStatusApiEnum;
  data: FormDataV1PublicApiDto;
  version: string;
  latitude?: number;
  longitude?: number;
  requiredGuestImages: GuestPictureTypeApiEnum[];
  requiredBookingContract: boolean;
  signedContract: boolean;
  minAgeToRequireImages: number;
  requireContactDataFromAllGuests: boolean;
  overallProgress?: number;
  contractStatus?: ContractSignatureStatus;
  rejectionReason?: string;
  // Story 28-4: full bilingual + multi-status messages map shipped on every
  // public form fetch. Always emitted (possibly `{}`). Outer key is the form
  // status (SENT, COMPLETED, ACCEPTED). Inner key is the locale (en, es).
  // Missing keys fall back to the form-app's i18n defaults at render time.
  formMessages?: Record<string, Record<string, string>>;
  brandSlug?: string | null;
}

export interface FormDataV1PublicApiDto {
  mainGuest: MainGuestV1PublicApiDto;
  otherGuests: GuestV1PublicApiDto[];
  vehicles: VehicleDataWithParkingSpaceV1ApiDto[];
  pets: PetDataV1ApiDto[];
}

export interface GuestV1PublicApiDto {
  guestNumId: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: Gender;
  idType?: IdTypeApiEnum;
  id?: string;
  idIssuingCountry?: CountryApiEnum;
  telephoneNumber?: string;
  email?: string;
  birthDate?: string;
  acceptTerms?: boolean;
  acceptTermsTimestamp?: string;
  acceptPrivacy?: boolean;
  acceptPrivacyTimestamp?: string;
  consentDataStorage?: boolean;
  consentDataStorageTimestamp?: string;
  consentMarketing?: boolean;
  consentMarketingTimestamp?: string;
  files?: FileV1PublicApiDto[];
  progress?: number;
  // Story 20-1 Phase 1 (refresh-resilient prefill, supersedes Decision 23b POST-only):
  // attached by PublicApiImpl._getFormData per guest when the lookup-key triple matches
  // a non-suppressed guest_profiles row. Absent on miss/suppressed.
  profileMatch?: import("./guest").ProfileMatch | null;
}

export interface MainGuestV1PublicApiDto extends GuestV1PublicApiDto {
  email: string;
}


export interface FileV1PublicApiDto {
  type: GuestPictureTypeApiEnum;
  url?: string;
}

export interface VehicleDataV1ApiDto {
  type: VehicleType;
  model: string;
  plateNr: string;
  parkingSpace?: string;
}

export interface VehicleDataWithParkingSpaceV1ApiDto {
  type: VehicleType;
  model: string;
  plateNr: string;
  parkingSpace?: string;
  parkingRequested?: boolean;
}

export interface PetDataV1ApiDto {
  type: PetType
  race: string;
}

export interface PublicFormDataAndVersionApiDto {
  data: FormDataV1PublicApiDto;
  version: string;
}

export interface VersionApiDto {
  version: string;
}

export interface GuestAndFormVersionApiDto {
  guest: GuestV1WithoutFilesPublicApiDto;
  version: string;
}

export interface GuestV1WithoutFilesPublicApiDto {
  firstName: string;
  middleName?: string;
  lastName: string;
  gender: Gender;
  idType: IdTypeApiEnum;
  id: string;
  idIssuingCountry: CountryApiEnum;
  telephoneNumber: string;
  email: string;
  birthDate: string;
  acceptTerms?: boolean;
  acceptTermsTimestamp?: string;
  acceptPrivacy?: boolean;
  acceptPrivacyTimestamp?: string;
  consentDataStorage?: boolean;
  consentDataStorageTimestamp?: string;
  consentMarketing?: boolean;
  consentMarketingTimestamp?: string;
}

export interface VehicleAndFormVersionApiDto {
  vehicle: VehicleDataV1ApiDto;
  version: string;
}

export interface PetAndFormVersionApiDto {
  pet: PetDataV1ApiDto;
  version: string;
}

export interface PictureUploadApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
  data: string; // Base64 string (without the data: prefix)
}

export interface PictureUploadWithGuestNumIdApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
  guestNumId: number;
  data: string;
}

export interface GuestPictureIdApiDto {
  type: GuestPictureTypeApiEnum;
  guestNumId: number;
}

export interface PresignedUrlRequestApiDto {
  mime: MimeApiEnum;
  type: GuestPictureTypeApiEnum;
}

export interface PresignedUrlApiDto {
  url: string;
  key: string;
}

export interface ContentTypeApiDto {
  contentType: MimeApiEnum;
}

export interface GuestImageRegistrationApiDto {
  s3Key: string;
  type: GuestPictureTypeApiEnum;
}

export interface PersonIdApiDto {
  id: string;
  idType: IdTypeApiEnum;
  idIssuingCountry: CountryApiEnum;
}

export interface ResultApiDto {
  result: boolean;
}

export interface ContractFileDto {
  fileId: string;
  filename: string;
  url: string;
}

export interface ContractApiDto {
  generalTerms: string;
  files?: ContractFileDto[];
}

export interface ContractSignatureApiDto {
  formVersion: string;
  s3Key: string;
  contentType: MimeApiEnum;
  ipAddress: string;
  deviceInfo: string;
}

// Listing Public Properties
export interface ListingPublicPropertiesApiDto {
  title: string;
  defaultCheckinTime?: string; // example: "15:00"
  defaultCheckoutTime?: string; // example: "12:00"
  maxPeople: number;
}

// Guest Check-in Request
export interface GuestCheckinRequestApiDto {
  contactEmail: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
}

// Guest Check-in Response
export interface GuestCheckinResponseApiDto {
  publicId: string;
}
